import { posthog } from '@/providers/posthog';
import { createRecipe } from '@/api/recipes';
import { FF_NEW_USER_WORKFLOW_LANDING } from '@/consts/featureFlags';
import { log } from '@/logger/logger.ts';

const logger = log.getLogger('newUserLanding');

/** The user waits on the sign-in spinner for this, and the request retries three times
 *  underneath, so without a limit a bad connection can hold them there for a long time. */
const CREATE_WORKFLOW_WAIT_MS = 5000;

/** Why a user ended up where they did. Sent to analytics so the two groups can be compared. */
export type NewUserLandingOutcome = 'workflow' | 'control' | 'flag_missing' | 'create_failed';

export interface NewUserLanding {
  /** Where to send the user, or null to keep the file browser landing they had before. */
  route: string | null;
  outcome: NewUserLandingOutcome;
}

/** Resolves to null if `promise` has not settled in time. The abandoned work is left to finish. */
function withDeadline<T>(promise: Promise<T>, waitMs: number): Promise<T | null> {
  const deadline = new Promise<null>((resolve) => setTimeout(() => resolve(null), waitMs));
  return Promise.race([promise, deadline]);
}

/**
 * Reads the flag from whatever PostHog already holds. Right after sign up that is often still the
 * set of answers for the visitor this person was a moment ago, because their own set has not
 * arrived yet. That is fine here. The split is random either way, and the group we picked is saved
 * on the new_user_landing event, so nothing later has to work it out again.
 *
 * For the same reason, do not read this flag anywhere else. Once the person's own answers arrive it
 * can say the opposite for them.
 *
 * undefined means we have no answer, which happens when PostHog cannot be reached at all.
 */
function isLandingFlagOn(): boolean | undefined {
  try {
    return posthog.isFeatureEnabled(FF_NEW_USER_WORKFLOW_LANDING);
  } catch (error) {
    logger.error('Could not read the new user landing flag', error);
    return undefined;
  }
}

/**
 * Where a user who has just signed up should land. A null route means keep the file browser,
 * which is where all of them landed before this experiment.
 */
export async function getNewUserLanding(): Promise<NewUserLanding> {
  const isEnabled = isLandingFlagOn();
  // Kept apart from the control group, so a lot of these tells us PostHog is not reaching people.
  if (isEnabled === undefined) {
    return { route: null, outcome: 'flag_missing' };
  }
  if (!isEnabled) {
    return { route: null, outcome: 'control' };
  }

  try {
    const workflow = await withDeadline(createRecipe(), CREATE_WORKFLOW_WAIT_MS);
    if (!workflow) {
      logger.warn('Creating the first workflow took too long, sending the new user to the file browser');
      return { route: null, outcome: 'create_failed' };
    }

    // ?new=true is what starts the onboarding on the workflow page, see
    // useWelcomeTemplateGalleryTour. Without it the user just gets an empty canvas.
    return { route: `/flow/${workflow.id}?new=true`, outcome: 'workflow' };
  } catch (error) {
    logger.error('Failed to send a new user to a workflow, falling back to the file browser', error);
    return { route: null, outcome: 'create_failed' };
  }
}
